/**
 * Event cards — system messages another service posted into the room: a call
 * booked, moved, cancelled, about to start.
 *
 * The server sends one generic shape for all of them
 * (`content_type: "EVENT_CARD"`, the card in `content_attributes`), and this
 * renders it generically. Nothing here switches on which event it is: a new
 * event type from the backend must look right without a FlexBoard release.
 *
 * Everything is defensive and nothing throws. The payload is validated
 * server-side, but a card that fails to normalise falls back to the plain
 * system line it would have been before this existed, which is always better
 * than a blank bubble.
 */

export const EVENT_CARD_CONTENT_TYPE = "EVENT_CARD";

const TONES = ["success", "info", "warning", "danger", "neutral"];
const STYLES = ["primary", "secondary", "danger"];

/** FlexBoard is the coach console, so it reads the staff side of a card. */
const AUDIENCE = "staff";

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const text = (value) => (typeof value === "string" ? value.trim() : "");

const parseInstant = (value) => {
  if (typeof value !== "string") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const DAY_FORMAT = { weekday: "short", day: "numeric", month: "short" };
const TIME_FORMAT = { hour: "numeric", minute: "2-digit" };

const sameLocalDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * "Wed, 14 Oct, 5:00 PM", in the viewer's own timezone. The server sends UTC
 * precisely so that this is where the clock time is decided.
 *
 * @param {Date} date
 * @returns {string}
 */
export const formatEventDateTime = (date) =>
  `${date.toLocaleDateString([], DAY_FORMAT)}, ${date.toLocaleTimeString([], TIME_FORMAT)}`;

/**
 * "Wed, 14 Oct, 5:00 PM – 5:30 PM"; the day is repeated only when the range
 * crosses midnight.
 *
 * @param {Date} start
 * @param {Date} end
 * @returns {string}
 */
export const formatEventDateRange = (start, end) => {
  const until = sameLocalDay(start, end)
    ? end.toLocaleTimeString([], TIME_FORMAT)
    : formatEventDateTime(end);
  return `${formatEventDateTime(start)} – ${until}`;
};

/**
 * One field's display string. An unknown `value_type`, or a datetime that will
 * not parse, shows the raw value rather than nothing.
 *
 * @param {Object} field
 * @returns {string}
 */
export const formatEventFieldValue = (field) => {
  const raw = text(field?.value);
  const start = parseInstant(field?.value);
  if (field?.value_type === "datetime" && start) {
    return formatEventDateTime(start);
  }
  if (field?.value_type === "datetime_range" && start) {
    const end = parseInstant(field?.value_end);
    return end ? formatEventDateRange(start, end) : formatEventDateTime(start);
  }
  return raw;
};

const normalizeEntity = (entity) => {
  if (!isObject(entity)) return null;
  const type = text(entity.type);
  const id = entity.id == null ? "" : String(entity.id);
  return type && id ? { type, id } : null;
};

const normalizeAction = (action) => {
  if (!isObject(action) || !isObject(action.target)) return null;
  const label = text(action.label);
  const routeKey = text(action.target.route_key);
  if (!label || !routeKey) return null;
  return {
    key: text(action.key) || routeKey,
    label,
    style: STYLES.includes(action.style) ? action.style : "secondary",
    routeKey,
    params: isObject(action.target.params) ? action.target.params : {},
  };
};

/**
 * Where each `route_key` goes in FlexBoard. An ALLOWLIST: a card can only ever
 * open a screen named here, with its params encoded — it can never navigate to
 * a string it carries. A key that is not listed has no destination, and its
 * button is not rendered.
 */
const ROUTES = {
  booking: (params) => {
    const bookingId = text(String(params.booking_id ?? ""));
    if (!bookingId) return null;
    const query = new URLSearchParams({ bookingId });
    // Optional, and only hints: which month to load and whose calendar to open.
    if (text(params.start_time)) query.set("start", params.start_time);
    if (text(params.coach_id)) query.set("coach", params.coach_id);
    return `/bookings?${query.toString()}`;
  },
};

/**
 * @param {{ routeKey: string, params: Object }} action
 * @returns {string|null} An in-app path, or null when this build has no screen for it.
 */
export const resolveEventCardRoute = (action) => {
  const build = ROUTES[action?.routeKey];
  return build ? build(action.params || {}) : null;
};

/**
 * @typedef {Object} EventCard
 * @property {string} event - e.g. "booking.confirmed". Informational only.
 * @property {{ type: string, id: string }|null} entity
 * @property {{ type: string, id: string }[]} supersedes
 * @property {string} title
 * @property {{ label: string, tone: string }|null} status
 * @property {{ label: string, value: string }[]} fields
 * @property {string} text - The sentence for this audience.
 * @property {{ key: string, label: string, style: string, to: string }[]} actions
 * @property {boolean} superseded - The server has retired this card.
 */

/**
 * @param {Object} message - Raw server message.
 * @returns {EventCard|null} Null when this is not a renderable event card.
 */
export const normalizeEventCard = (message) => {
  if (message?.content_type !== EVENT_CARD_CONTENT_TYPE) return null;
  const attrs = message.content_attributes;
  if (!isObject(attrs) || !isObject(attrs.card)) return null;

  const title = text(attrs.card.title);
  if (!title) return null;

  const rawStatus = attrs.card.status;
  const status =
    isObject(rawStatus) && text(rawStatus.label)
      ? {
          label: text(rawStatus.label),
          tone: TONES.includes(rawStatus.tone) ? rawStatus.tone : "neutral",
        }
      : null;

  const fields = (Array.isArray(attrs.card.fields) ? attrs.card.fields : [])
    .filter(isObject)
    .map((field) => ({
      label: text(field.label),
      value: formatEventFieldValue(field),
    }))
    .filter((field) => field.label && field.value);

  const audience = isObject(attrs.audiences) ? attrs.audiences[AUDIENCE] : null;
  const actions = (isObject(audience) && Array.isArray(audience.actions) ? audience.actions : [])
    .map(normalizeAction)
    .filter(Boolean)
    .map((action) => ({ ...action, to: resolveEventCardRoute(action) }))
    .filter((action) => action.to)
    .map(({ key, label, style, to }) => ({ key, label, style, to }));

  return {
    event: text(attrs.event),
    entity: normalizeEntity(attrs.entity),
    supersedes: (Array.isArray(attrs.supersedes) ? attrs.supersedes : [])
      .map(normalizeEntity)
      .filter(Boolean),
    title,
    status,
    fields,
    // No staff wording on this card: the neutral sentence is the next best.
    text: (isObject(audience) && text(audience.text)) || text(message.content),
    actions,
    superseded: attrs.superseded_by != null,
  };
};

const entityKey = (source, entity) => `${source}\u0000${entity.type}\u0000${entity.id}`;

/**
 * Ids of the loaded event cards a NEWER loaded card has made stale.
 *
 * The server is the authority (`superseded_by`), but it stamps the old card a
 * beat after the new one arrives, and the old card may be on a page this view
 * will not refetch. Every card names its own `entity` and the entities it
 * `supersedes`, so the thread can dim the older ones the instant the new one
 * lands. Messages must be in `seq` order, oldest first.
 *
 * @param {Object[]} messages
 * @returns {Set<string>}
 */
export const findSupersededEventCards = (messages) => {
  const latestByEntity = new Map();
  const stale = new Set();
  for (const message of messages || []) {
    const card = normalizeEventCard(message);
    if (!card || !card.entity) continue;
    const source = text(message.content_attributes?.source);
    for (const entity of [card.entity, ...card.supersedes]) {
      const key = entityKey(source, entity);
      const previous = latestByEntity.get(key);
      if (previous != null && previous !== message.id) stale.add(previous);
    }
    latestByEntity.set(entityKey(source, card.entity), message.id);
  }
  return stale;
};
